/**
 * StagesStep — which candidacy stages of the source search to bring (epic
 * 2026-10-15-thrive-search-import, sub-task 03).
 *
 * One chip per stage the search has, with its count, in the ladder order the
 * preview endpoint returns (`ThrivePreviewResponse.stages`: the ladder, then
 * the off-ladder stages A-Z with Rejected last, then Unknown). Every chip
 * starts checked. The line under them says what the button will import:
 * "N of M candidacies in K of S stages".
 *
 * WHEN THE COUNT IS NOT EXACT. The counts may come from the search
 * dashboard's report cache (up to 24 h old) or from a live read whose pages
 * did not add up; the import itself reads Thrive live. Then N is "about N",
 * and a note says why, rather than promising a number the list may not have.
 *
 * Tokenized: token color utilities + `text-*` roles. No inline styles.
 */

import type { ThrivePreviewResponse } from "@nu-assist/contracts";
import { Button, Spinner } from "@nu-assist/ui";
import { Link } from "react-router-dom";

import { stageBgClass } from "../../../../components/stages/chartPalette";
import { formatDate } from "../../../../lib/format/date";
import { searchDetailPath } from "../../../../routing/paths";
import { type PreviewFailure, countsAreExact } from "../useThriveImport";

export interface StagesStepProps {
  sourceId: string;
  preview: ThrivePreviewResponse | undefined;
  loading: boolean;
  failure: PreviewFailure | null;
  onRetry: () => void;
  isChecked: (stage: string) => boolean;
  onToggle: (stage: string) => void;
  checkedStages: readonly string[];
  checkedCount: number;
}

/** `N`, or `about N` when the counts are not what the import will read. */
export function candidacyCountLabel(count: number, exact: boolean): string {
  return exact ? count.toLocaleString() : `about ${count.toLocaleString()}`;
}

export function StagesStep({
  sourceId,
  preview,
  loading,
  failure,
  onRetry,
  isChecked,
  onToggle,
  checkedStages,
  checkedCount,
}: StagesStepProps) {
  if (failure) {
    return (
      <div data-testid="thrive-stages-step" className="flex flex-col gap-3">
        <div
          data-testid="thrive-stages-error"
          role="alert"
          className="flex flex-col gap-2 rounded-md border-l-4 border-error-strong bg-error-muted p-4"
        >
          <p className="text-caption text-text">{failure.message}</p>
          <div className="flex items-center gap-3">
            {failure.canRetry ? (
              <Button variant="secondary" onClick={onRetry} data-testid="thrive-stages-retry">
                Retry
              </Button>
            ) : null}
            {failure.openDashboard ? (
              <Link
                to={searchDetailPath(sourceId)}
                data-testid="thrive-stages-dashboard"
                className="text-caption text-accent hover:underline"
              >
                Open the search dashboard
              </Link>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  if (loading || !preview) {
    return (
      <div
        data-testid="thrive-stages-loading"
        className="flex items-center gap-2 text-caption text-text-secondary"
      >
        <span className="text-accent">
          <Spinner size="sm" label="Counting candidacies" />
        </span>
        <span aria-hidden="true">Counting candidacies in Thrive…</span>
      </div>
    );
  }

  const stages = preview.stages ?? [];
  if (preview.total === 0 || stages.length === 0) {
    return (
      <div data-testid="thrive-stages-step" className="flex flex-col gap-3">
        <p data-testid="thrive-stages-empty" className="text-caption text-warning-ink">
          This search has no candidacies to import.
        </p>
      </div>
    );
  }

  const exact = countsAreExact(preview);
  return (
    <div data-testid="thrive-stages-step" className="flex flex-col gap-3">
      <fieldset className="flex flex-col gap-3">
        <legend className="mb-3 text-label text-text">Stages to bring</legend>
        <div className="flex flex-wrap items-center gap-2">
          {stages.map(({ stage, count }) => {
            const pressed = isChecked(stage);
            return (
              <button
                key={stage}
                type="button"
                data-testid="thrive-stage-chip"
                data-stage={stage}
                aria-pressed={pressed}
                onClick={() => onToggle(stage)}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-label transition-colors ${
                  pressed
                    ? "border-accent bg-accent-muted text-accent"
                    : "border-border text-text-secondary hover:border-border-hover hover:text-text"
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`h-2 w-2 shrink-0 rounded-full ${stageBgClass(stage)}`}
                />
                {stage}
                <span className="tabular-nums text-text-muted">{count.toLocaleString()}</span>
              </button>
            );
          })}
        </div>
      </fieldset>
      <p data-testid="thrive-stages-summary" className="text-caption text-text-secondary">
        {candidacyCountLabel(checkedCount, exact)} of {candidacyCountLabel(preview.total, exact)}{" "}
        candidacies in {checkedStages.length} of {stages.length} stages
      </p>
      {checkedStages.length === 0 ? (
        <p data-testid="thrive-stages-none" className="text-caption text-warning-ink">
          Choose at least one stage to import.
        </p>
      ) : null}
      {exact ? null : (
        <p data-testid="thrive-stages-approximate" className="text-caption text-text-muted">
          {preview.counts_source === "cache"
            ? `Counted ${formatDate(preview.counted_at)} for the search dashboard.`
            : "Thrive's pages changed while counting."}{" "}
          The import reads Thrive when it runs, so the list can hold a different number.
        </p>
      )}
    </div>
  );
}
